/**
 * Pricing Card Component
 * Individual tier card with features and CTA
 */

import { Check, Crown, Star, Zap, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatPrice, getYearlySavings, YEARLY_DISCOUNT_PERCENT } from '../utils/constants';
import type { PricingTier, BillingPeriod, SubscriptionTier, SubscriptionSource } from '../types';

// Tier icons
const TIER_ICONS = {
  free: Zap,
  pro: Crown,
  pro_plus: Star,
} as const;

interface PricingCardProps {
  tier: PricingTier;
  billingPeriod: BillingPeriod;
  currentTier: SubscriptionTier;
  subscriptionSource: SubscriptionSource;
  isLoading: boolean;
  onUpgrade: (tier: 'pro' | 'pro_plus') => void;
  animationDelay?: number;
}

export function PricingCard({
  tier,
  billingPeriod,
  currentTier,
  subscriptionSource,
  isLoading,
  onUpgrade,
  animationDelay = 0,
}: PricingCardProps) {
  const { t } = useLanguage();

  const isCurrentTier = currentTier === tier.id;
  const canUpgrade = subscriptionSource !== 'apple' && subscriptionSource !== 'google';
  const isPopular = tier.popular;
  const Icon = TIER_ICONS[tier.id];

  // Calculate prices
  const displayPrice = billingPeriod === 'yearly' ? tier.yearlyPrice / 12 : tier.monthlyPrice;
  const yearlyTotal = tier.yearlyPrice;
  const savings = getYearlySavings(tier.monthlyPrice, tier.yearlyPrice);

  return (
    <div
      className={`
        relative rounded-2xl overflow-hidden transition-all duration-300
        ${isPopular ? 'scale-[1.02] z-10' : ''}
        ${isCurrentTier
          ? 'border-2 border-primary-500 bg-primary-50/50'
          : isPopular
            ? 'border-2 border-action-light bg-white shadow-xl'
            : 'border border-gray-200 bg-white hover:shadow-lg hover:border-primary-200 hover:-translate-y-1'
        }
        animate-fade-in
      `}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      {/* Popular badge */}
      {isPopular && !isCurrentTier && (
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-action-light to-action-dark text-center py-2">
          <span className="text-xs font-bold text-white uppercase tracking-wide">
            {t('upgrade.pricing.mostPopular', 'Most Popular')}
          </span>
        </div>
      )}

      {/* Current plan badge */}
      {isCurrentTier && (
        <div className="absolute top-0 left-0 right-0 bg-primary-600 text-center py-2">
          <span className="text-xs font-bold text-white uppercase tracking-wide">
            {t('upgrade.pricing.currentPlan', 'Current Plan')}
          </span>
        </div>
      )}

      {/* Content */}
      <div className={`p-6 ${isPopular || isCurrentTier ? 'pt-12' : ''}`}>
        {/* Tier icon and name */}
        <div className="text-center mb-6">
          <div
            className={`
              inline-flex items-center justify-center w-14 h-14 rounded-xl mb-4
              ${isCurrentTier
                ? 'bg-primary-100 text-primary-600'
                : isPopular
                  ? 'bg-action-light/20 text-action-dark'
                  : 'bg-gray-100 text-gray-600'
              }
            `}
          >
            <Icon className="w-7 h-7" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
          <p className="text-sm text-gray-500 mt-1">{tier.description}</p>
        </div>

        {/* Price */}
        <div className="text-center mb-6">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-4xl font-bold text-gray-900">
              {tier.monthlyPrice === 0 ? '$0' : formatPrice(displayPrice)}
            </span>
            {tier.monthlyPrice > 0 && (
              <span className="text-gray-500">/month</span>
            )}
          </div>

          {/* Yearly pricing info */}
          {tier.monthlyPrice > 0 && billingPeriod === 'yearly' && (
            <div className="mt-2 space-y-1">
              <p className="text-sm text-gray-500">
                {formatPrice(yearlyTotal)}/year
              </p>
              <p className="text-xs text-green-600 font-medium">
                {t('upgrade.pricing.save', 'Save')} {formatPrice(savings)} ({YEARLY_DISCOUNT_PERCENT}% {t('upgrade.pricing.off', 'off')})
              </p>
            </div>
          )}

          {/* Forever free badge */}
          {tier.monthlyPrice === 0 && (
            <p className="text-sm text-gray-500 mt-1">{t('upgrade.pricing.forever', 'forever')}</p>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-3 mb-6">
          {tier.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3">
              <div
                className={`
                  flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5
                  ${isCurrentTier
                    ? 'bg-primary-100 text-primary-600'
                    : isPopular
                      ? 'bg-action-light/20 text-action-dark'
                      : 'bg-gray-100 text-gray-600'
                  }
                `}
              >
                <Check className="w-3 h-3" />
              </div>
              <span className="text-sm text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        {tier.id !== 'free' && !isCurrentTier && canUpgrade && (
          <button
            onClick={() => onUpgrade(tier.id as 'pro' | 'pro_plus')}
            disabled={isLoading}
            className={`
              w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50
              ${isPopular
                ? 'bg-gradient-to-r from-action-light to-action-dark text-white hover:from-action-dark hover:to-action-dark shadow-lg shadow-action-light/30'
                : 'bg-primary-600 text-white hover:bg-primary-700'
              }
            `}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('upgrade.pricing.loading', 'Loading...')}
              </span>
            ) : (
              t('upgrade.pricing.upgradeTo', `Upgrade to ${tier.name}`)
            )}
          </button>
        )}

        {/* Current plan indicator */}
        {isCurrentTier && (
          <div className="text-center py-3 px-4 rounded-xl bg-primary-100 text-primary-700 font-medium">
            {t('upgrade.pricing.yourPlan', 'Your current plan')}
          </div>
        )}

        {/* Free tier CTA */}
        {tier.id === 'free' && !isCurrentTier && (
          <div className="text-center py-3 px-4 rounded-xl bg-gray-100 text-gray-500 font-medium">
            {t('upgrade.pricing.freeTier', 'Basic features')}
          </div>
        )}

        {/* App store subscription notice */}
        {tier.id !== 'free' && !isCurrentTier && !canUpgrade && (
          <div className="text-center py-3 px-4 rounded-xl bg-gray-100 text-gray-500 text-sm">
            {t('upgrade.pricing.manageInStore', 'Manage in your app store')}
          </div>
        )}
      </div>
    </div>
  );
}
